import React, { Fragment, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';

/* Helpers */
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSbTileAbTestTracking } from '../useSbTileAbTestTracking';

/* Components */
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbRespSearchResultGrid, PROMINENT_TILE } from './SbRespSearchResultGrid';

import styles from './sbRespSearchResultContainer.scss';

import { useSearchCorrections } from '../useSearchCorrections';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 2;
const ITEMS_PER_ROW_FOR_SB_TOP_NAV = 3;
const LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV = ITEMS_PER_ROW_FOR_SB_TOP_NAV * 2;

const SbRespSearchResultContainerComponent = props => {
    const {
        currency,
        showMeasurements,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        itemSearch,
        viewer,
        relay,
        isClient,
        user,
        gridType,
    } = props;

    const { edges: items = [] } = itemSearch || {};
    const {
        isTileAdEnabled,
        isFavoritesEnabled,
        isSeoQuicklookEnabled,
        isVisuallySimilarEnabled,
    } = viewer;

    const userId = user ? user.serviceId : null;

    const { isSbTileCarouselTest, isSbTopNavVariant } = useContext(ServerVarsContext);
    useSbTileAbTestTracking();

    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });
    const [measurementUnit] = useMeasurementUnit({ itemSearch, user });
    /* eslint-disable react/prop-types, react/display-name */
    return (
        <div className={styles.container}>
            <FavoritesProvider
                disable={!isClient}
                environment={relay.environment}
                itemIds={items.map(({ node }) => get(node, 'item.serviceId'))}
                location={locations.SEARCH_BROWSE}
                userId={userId}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider
                                itemSearch={itemSearch}
                                searchCorrectionTerm={searchCorrectionTerm}
                                viewer={viewer}
                            >
                                {({ fireItemImpressionTracking, fireItemClickTracking }) => (
                                    <SbRespSearchResultGrid items={items} type={gridType}>
                                        {({ item, index, customTileWidthClass, tileType }) => {
                                            const quickViewProps = isSeoQuicklookEnabled
                                                ? quickView
                                                : null;

                                            const tileImageSize =
                                                tileType === PROMINENT_TILE ? 'large' : undefined;

                                            return (
                                                <Fragment>
                                                    {isTileAdEnabled && (
                                                        <SbSharedProductAdWrapper
                                                            index={index}
                                                            itemSearch={itemSearch}
                                                            user={user}
                                                            viewer={viewer}
                                                        />
                                                    )}
                                                    <SharedItemTile
                                                        isVisuallySimilarEnabled={
                                                            isVisuallySimilarEnabled &&
                                                            itemSearch.pageType !==
                                                                pageTypes.EXCLUSIVE
                                                        }
                                                        item={item}
                                                        favorites={isFavoritesEnabled && favorites}
                                                        index={index}
                                                        currency={currency}
                                                        isMobile={false}
                                                        isSbTileCarouselTest={isSbTileCarouselTest}
                                                        showInternalSellerName={
                                                            showInternalSellerName
                                                        }
                                                        isClient={isClient}
                                                        showSellerName={showSellerName}
                                                        showCreatorName={showCreatorName}
                                                        showMeasurements={showMeasurements}
                                                        quickViewProps={quickViewProps}
                                                        lazyLoadStartIndex={
                                                            isSbTopNavVariant
                                                                ? LAZY_LOAD_ITEM_TILE_INDEX_FOR_SB_TOP_NAV
                                                                : LAZY_LOAD_ITEM_TILE_INDEX
                                                        }
                                                        imageLoadVerticalOffset={
                                                            VERTICAL_OFFSET_SB_RESP_IMG
                                                        }
                                                        onContentVisible={() =>
                                                            fireItemImpressionTracking(index)
                                                        }
                                                        onClick={() => fireItemClickTracking(index)}
                                                        measurementUnit={measurementUnit}
                                                        customTileWidthClass={customTileWidthClass}
                                                        imageSize={tileImageSize}
                                                        viewer={viewer}
                                                        tileType={tileType}
                                                    />
                                                </Fragment>
                                            );
                                        }}
                                    </SbRespSearchResultGrid>
                                )}
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
    /* eslint-enable react/prop-types, react/display-name */
};

SbRespSearchResultContainerComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    showMeasurements: PropTypes.bool.isRequired,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    user: PropTypes.object,
    gridType: PropTypes.string,
};

export const SbRespSearchResultContainer = createFragmentContainer(
    SbRespSearchResultContainerComponent,
    {
        viewer: graphql`
            fragment SbRespSearchResultContainer_viewer on Viewer {
                isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
                isTileAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFavoritesEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isVisuallySimilarEnabled: isEnabledByLocale(featureName: "VISUALLY_SIMILAR")
                isSbTradeFreeShippingTestDisabled: isEnabled(key: "disableSbTradeFreeShippingTest")
                    @include(if: $isTrade)
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
                ...SbSharedProductAdWrapper_viewer
            }
        `,
        user: graphql`
            fragment SbRespSearchResultContainer_user on User {
                serviceId
                ...useMeasurementUnit_user
                ...SbSharedProductAdWrapper_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
                ...useMeasurementUnit_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                ...useSearchCorrections_itemSearch
                ...SbSharedProductAdWrapper_itemSearch
                pageType
                edges {
                    node {
                        item {
                            serviceId
                            ...SharedItemTile_item
                        }
                    }
                }
            }
        `,
    }
);
